// src/app/modules/Devices/utils.js
import { useCallback, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Moment from 'moment';

/** Active org + whether the signed-in user may change devices (admin / manager / super admin). */
export function useDeviceAccess() {
  const { org, user } = useSelector((s) => ({ org: s.auth.organization, user: s.auth.user }), shallowEqual);
  const role = org?.membership?.role || org?.role || '';
  const canManage = ['admin', 'manager'].includes(role) || user?.user_type === 'super_admin';
  return { org, orgId: org?.organization_id, canManage, locations: org?.locations || [] };
}

/** Run `fn` now and every `ms` while mounted (and while `enabled`). Pauses when the tab is hidden. */
export function usePolling(fn, ms, enabled = true) {
  const saved = useRef(fn);
  useEffect(() => { saved.current = fn; }, [fn]);
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => { if (!document.hidden) saved.current(); };
    saved.current();
    const id = setInterval(tick, ms);
    return () => clearInterval(id);
  }, [ms, enabled]);
}

/** Stable callback that always sees fresh props/state. */
export function useEvent(fn) {
  const ref = useRef(fn);
  useEffect(() => { ref.current = fn; });
  return useCallback((...args) => ref.current(...args), []);
}

export const lastSeen = (iso) => (iso ? Moment(iso).fromNow() : 'Never');
export const dateTime = (iso) => (iso ? Moment(iso).format('D MMM YYYY, HH:mm:ss') : '—');
export const shortTime = (iso) => (iso ? Moment(iso).format('HH:mm:ss') : '');
export const shortDate = (iso) => (iso ? Moment(iso).format('D MMM, HH:mm') : '—');

export const SCAN_MODES = {
  toggle: { label: 'Check in & out', hint: 'First scan checks in, the next one checks out' },
  check_in_only: { label: 'Check-in only', hint: 'For entrance-only terminals' },
  check_out_only: { label: 'Check-out only', hint: 'For exit-only terminals' },
};

export const HEALTH_FLAGS = {
  holding_offline_events: { label: 'Holding offline scans', tone: 'warning' },
  clock_unverified: { label: 'Clock not verified', tone: 'warning' },
  sensor_fault: { label: 'Sensor fault', tone: 'danger' },
  sd_fault: { label: 'SD card fault', tone: 'danger' },
};

export const OUTCOMES = {
  check_in: { label: 'Checked in', tone: 'success' },
  check_out: { label: 'Checked out', tone: 'success' },
  ignored_repeat: { label: 'Repeat scan', tone: 'neutral' },
  unknown_uid: { label: 'Unknown fingerprint', tone: 'warning' },
  revoked_uid: { label: 'Deletion pending', tone: 'warning' },
  member_inactive: { label: 'Member inactive', tone: 'warning' },
  rejected: { label: 'Not allowed', tone: 'warning' },
  needs_review: { label: 'Needs review', tone: 'danger' },
  dismissed: { label: 'Dismissed', tone: 'neutral' },
  error: { label: 'Error', tone: 'danger' },
  pending: { label: 'Processing', tone: 'neutral' },
};

export const REVIEW_REASONS = {
  untrusted_clock: 'Device clock was not verified when this offline scan happened',
  future_timestamp: 'Scan time is in the future',
  out_of_order: 'Older than the member’s latest shift',
  unknown_uid: 'Fingerprint is not linked to anyone',
  processing_error: 'Could not be processed',
};

export const OUTCOME_DETAILS = {
  already_on_shift: 'Already on shift',
  no_open_shift: 'No open shift to close',
  previous_shift_auto_closed: 'Previous shift auto-closed (missed check-out)',
  stale_shift_closed: 'Stale shift auto-closed',
  delete_pending: 'Deletion queued for this fingerprint',
  no_staff_record: 'Member has no staff record',
  membership_removed: 'Member was removed',
  membership_suspended: 'Member is suspended',
  staff_record_revoked: 'Staff access revoked',
};

export const COMMAND_LABELS = {
  delete_uid: (c) => `Delete fingerprint #${c.uid}`,
  delete_all: () => 'Delete all fingerprints',
};

/** Signal strength bucket from dBm. */
export const signal = (rssi) => {
  if (rssi === null || rssi === undefined) return { label: '—', tone: 'neutral' };
  if (rssi >= -60) return { label: `Strong · ${rssi} dBm`, tone: 'success' };
  if (rssi >= -72) return { label: `Good · ${rssi} dBm`, tone: 'success' };
  if (rssi >= -82) return { label: `Weak · ${rssi} dBm`, tone: 'warning' };
  return { label: `Poor · ${rssi} dBm`, tone: 'danger' };
};
